import React, { useEffect, useState } from "react";
import { Map } from "immutable";
import { Icon } from "@showitinc/showit-wc";
import { Big } from "big.js";
import { PropNames } from "@showitinc/merge-utils";

import connectToStores from "./providers/connectToStores";
import SelectionStore from "../stores/SelectionStore";
import StageView from "./StageView";
import AppConstants from "../constants/AppConstants";
import { recursivelyCopySyncedPropsToMobile } from "../utils/SyncUtils";
import { gatherProps } from "../utils/Merge/MergeUtils.mjs";
import useOverlayDrag from "../hooks/useOverlayDrag";
import ActiveBlockContainerStore from "../stores/ActiveBlockContainerStore.mjs";
import UIActions from "../actions/UIActions";
import useStoreData from "../hooks/useStoreData";
import DesignViewStore from "../stores/DesignViewStore";
import { clampStageViewWidth } from "../utils/ZoomUtils";
import { isIOS } from "../constants/BrowserConstants";
import {
  MOBILE_STAGE_VIEW_RATIO,
  STAGE_SCALE_BREAKPOINT,
  STAGE_VIEW_MIN_WIDTH,
} from "../constants/UIConstants";

import styles from "./Stage.module.css";

const {
  BG_PROPERTY_FILL_TYPE,
  BG_PROPERTY_MEDIA_TYPE,
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  STAGE_VIEW_DESKTOP,
  STAGE_VIEW_MOBILE,
  STAGE_VIEW_SPLIT,
} = AppConstants;

const DRAG_OP = "si-split-resizer";
const { DESKTOP, MOBILE, LAYOUTS } = PropNames;

/**
 * Gets zoom setting for current layout
 * @param {*} store
 * @param {typeof LAYOUTS[number]} layout
 * @returns {number}
 */
const zoomGetter = (store, layout) => store.getIn(["stage", layout, "zoom"], 1);

/**
 * @callback CalculateWidth
 * @param {number} width
 */

/**
 * @param {number} movementX
 * @returns {CalculateWidth}
 */
const onDrag = (movementX) => (width) => {
  try {
    return new Big(width).plus(movementX).toNumber();
  } catch (err) {
    return width;
  }
};

const Stage = ({
  blockContainer,
  stage,
  viewState,
  selectedElementIds = Map(),
  showSelectLayer,
}) => {
  const [stageRef, setStageRef] = useState(null);

  const [mobileZoom] = useStoreData((storeData, update) => {
    storeData && update(zoomGetter(storeData, MOBILE));
  }, DesignViewStore);

  const [desktopZoom] = useStoreData((storeData, update) => {
    storeData && update(zoomGetter(storeData, DESKTOP));
  }, DesignViewStore);

  /**
   * Maintains zoom value to be passed through to stage views. Prevents zoom wiggle where the view will zoom and
   * become wider before being resized.
   */
  const [mobileDisplayZoom, setMobileDisplayZoom] = useState(mobileZoom);
  const [mobileWidth, setMobileWidth] = useState(
    AppConstants.STAGE_MOBILE_SPLIT_WIDTH,
  );
  const [startDrag] = useOverlayDrag(
    DRAG_OP,
    ({ movementX }) => setMobileWidth(onDrag(movementX)),
    () =>
      setMobileWidth((width) =>
        clampStageViewWidth(stageRef.clientWidth, width),
      ),
  );

  const selectedLayout = blockContainer.get("layout");
  const blocks = blockContainer.get("blocks");
  const selectedBlock = blockContainer.get("selectedBlock");
  const theme = blockContainer.get("theme");
  const [mergedTheme] = recursivelyCopySyncedPropsToMobile(theme);
  const BGData = blockContainer?.get("data") || Map();
  const mobileBG = gatherProps(
    BGData,
    MOBILE,
    [BG_PROPERTY_FILL_TYPE, BG_PROPERTY_MEDIA_TYPE],
    true,
  );
  const desktopBG = gatherProps(
    BGData,
    DESKTOP,
    [BG_PROPERTY_FILL_TYPE, BG_PROPERTY_MEDIA_TYPE],
    true,
  );

  const stageView = stage.get("view");
  const views = [];
  const isSplit = stageView === STAGE_VIEW_SPLIT;
  // a tablet starts desktop-only (see DesignViewStore), which fits like split
  const fitsOnLoad = isSplit || (isIOS && stageView === STAGE_VIEW_DESKTOP);

  if (isSplit || stageView === STAGE_VIEW_MOBILE) {
    views.push(
      <StageView
        layout={AppConstants.LAYOUT_MOBILE}
        key={AppConstants.LAYOUT_MOBILE}
        width={isSplit ? mobileWidth : "100%"}
        background={mobileBG}
        blockContainer={blockContainer}
        theme={mergedTheme}
        blocks={blocks}
        selectedBlock={selectedBlock}
        selectedElementIds={selectedElementIds}
        showSelectLayer={showSelectLayer}
        hasSelection={selectedLayout === LAYOUT_MOBILE}
        viewState={viewState}
        zoom={mobileDisplayZoom}
      />,
    );
  }

  if (isSplit) {
    views.push(
      <div
        className={styles.gutter}
        onPointerDown={startDrag}
        key="gutter"
        data-testid="stage-split-gutter"
      >
        <Icon type="dragHandle" />
      </div>,
    );
  }

  if (isSplit || stageView === STAGE_VIEW_DESKTOP) {
    views.push(
      <StageView
        layout={AppConstants.LAYOUT_DESKTOP}
        key={AppConstants.LAYOUT_DESKTOP}
        background={desktopBG}
        blockContainer={blockContainer}
        theme={theme}
        blocks={blocks}
        selectedBlock={selectedBlock}
        selectedElementIds={selectedElementIds}
        showSelectLayer={showSelectLayer}
        hasSelection={selectedLayout === LAYOUT_DESKTOP}
        viewState={viewState}
        zoom={desktopZoom}
      />,
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only fit on load - a view switch keeps the user's zoom
  useEffect(() => {
    if (stageRef && fitsOnLoad) {
      const stageWidth = stageRef.clientWidth;
      if (isSplit) {
        setMobileWidth(stageWidth * MOBILE_STAGE_VIEW_RATIO);
      }
      if (stageWidth < STAGE_SCALE_BREAKPOINT) {
        const container = ActiveBlockContainerStore.getContainer();

        // delay resizing to prevent incorrect zooming. todo: look into what causes this.
        if (isSplit) {
          setTimeout(
            () => UIActions.zoomToWidth(LAYOUT_MOBILE, container, false),
            10,
          );
        }
        setTimeout(
          () => UIActions.zoomToWidth(LAYOUT_DESKTOP, container, false),
          11,
        );
      }
    }
  }, [stageRef]);

  useEffect(() => {
    setMobileDisplayZoom(mobileZoom);
  }, [mobileZoom]);

  useEffect(() => {
    UIActions.stageWidthChange(mobileWidth);
  }, [mobileWidth]);

  const zoomStyles = {
    "--min-panel-width": `${STAGE_VIEW_MIN_WIDTH}px`,
    "--left-panel-size": isSplit ? `${Math.max(mobileWidth, 0)}px` : "100%",
  };

  return (
    <div
      className={`${styles["stage-view"]} ${styles[`stage-view-${stageView}`]}`}
      data-testid="stage"
      ref={setStageRef}
      style={zoomStyles}
    >
      {views}
    </div>
  );
};

export default connectToStores(Stage, [
  {
    store: SelectionStore,
    state: {
      selectedElementIds: SelectionStore.getElementIds,
      showSelectLayer: SelectionStore.getShowSelectLayer,
    },
  },
]);
